// ============================================
// Funnel Dashboard — Conversion Analytics
//
// Admin dashboard for viewing patient conversion
// funnel data. Design system compliant:
// DM Sans body, Instrument Serif titles,
// Rose #E11D48 accent, dark table headers,
// no framer-motion (pure CSS transitions).
// ============================================

import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { Loader2, TrendingDown, TrendingUp, BarChart3, RefreshCw } from 'lucide-react';
import type { FunnelEventType } from '../../../services/analytics/funnelTracker';
import { C_FUNNEL_EVENTS } from '../../../constants/collections';

// ── Types ──

interface FunnelRecord {
  event: FunnelEventType;
  sessionId: string;
  timestamp: Date;
  page: string;
  referrer: string;
  metadata: Record<string, string | number | boolean>;
}

type TimePeriod = 'today' | '7d' | '30d' | 'all';

// ── Funnel stage config ──

const FUNNEL_STAGES: { event: FunnelEventType; label: string; color: string }[] = [
  { event: 'PAGE_VIEW', label: 'Landing Page', color: '#3B82F6' },
  { event: 'CTA_CLICK', label: 'CTA Click', color: '#7C3AED' },
  { event: 'LAB_UPLOAD', label: 'Lab Upload', color: '#8B5CF6' },
  { event: 'TRIAGE_START', label: 'Triage Start', color: '#6366F1' },
  { event: 'TRIAGE_COMPLETE', label: 'Triage Complete', color: '#EC4899' },
  { event: 'PROTOCOL_VIEWED', label: 'Protocol Viewed', color: '#E11D48' },
  { event: 'CHECKOUT_STARTED', label: 'Checkout Started', color: '#F59E0B' },
  { event: 'PURCHASE_COMPLETE', label: 'Purchase', color: '#16A34A' },
];

const SECONDARY_EVENTS: { event: FunnelEventType; label: string }[] = [
  { event: 'ACCOUNT_CREATED', label: 'Account Created' },
  { event: 'PDF_DOWNLOADED', label: 'PDF Downloaded' },
];

// ── Helpers ──

function getPeriodStart(period: TimePeriod): Date | null {
  if (period === 'all') return null;
  const now = new Date();
  if (period === 'today') {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  if (period === '7d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return d;
  }
  // 30d
  const d = new Date(now);
  d.setDate(d.getDate() - 30);
  return d;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatPercent(n: number): string {
  if (isNaN(n) || !isFinite(n)) return '--';
  return `${n.toFixed(1)}%`;
}

// ── Component ──

export function FunnelDashboard() {
  const [records, setRecords] = useState<FunnelRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<TimePeriod>('7d');
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch from Firestore
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const ref = collection(db, C_FUNNEL_EVENTS);
        const start = getPeriodStart(period);

        const constraints = start
          ? [where('timestamp', '>=', Timestamp.fromDate(start)), orderBy('timestamp', 'desc')]
          : [orderBy('timestamp', 'desc')];

        const q = query(ref, ...constraints);
        const snap = await getDocs(q);

        if (cancelled) return;

        const items: FunnelRecord[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            event: data.event as FunnelEventType,
            sessionId: data.sessionId ?? '',
            timestamp: data.timestamp?.toDate?.() ?? new Date(),
            page: data.page ?? '',
            referrer: data.referrer ?? '',
            metadata: data.metadata ?? {},
          };
        });

        setRecords(items);
      } catch (err) {
        if (!cancelled) {
          console.error('[FunnelDashboard] Load failed:', err);
          setError('Failed to load funnel data. Check Firestore rules.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [period, refreshKey]);

  // Compute funnel counts — deduplicate by sessionId per stage
  const funnelData = useMemo(() => {
    const stageCounts: Record<FunnelEventType, number> = {} as Record<FunnelEventType, number>;
    const secondaryCounts: Record<FunnelEventType, number> = {} as Record<FunnelEventType, number>;

    // Count unique sessions per event type
    const sessionSets: Record<string, Set<string>> = {};
    for (const r of records) {
      if (!sessionSets[r.event]) sessionSets[r.event] = new Set();
      sessionSets[r.event].add(r.sessionId);
    }

    for (const stage of FUNNEL_STAGES) {
      stageCounts[stage.event] = sessionSets[stage.event]?.size ?? 0;
    }
    for (const sec of SECONDARY_EVENTS) {
      secondaryCounts[sec.event] = sessionSets[sec.event]?.size ?? 0;
    }

    // Compute conversion rates between adjacent stages
    const conversions: { from: string; to: string; rate: number; dropOff: number }[] = [];
    for (let i = 1; i < FUNNEL_STAGES.length; i++) {
      const prev = FUNNEL_STAGES[i - 1];
      const curr = FUNNEL_STAGES[i];
      const prevCount = stageCounts[prev.event];
      const currCount = stageCounts[curr.event];
      const rate = prevCount > 0 ? (currCount / prevCount) * 100 : 0;
      const dropOff = prevCount > 0 ? ((prevCount - currCount) / prevCount) * 100 : 0;
      conversions.push({
        from: prev.label,
        to: curr.label,
        rate,
        dropOff,
      });
    }

    // Overall conversion
    const topCount = stageCounts[FUNNEL_STAGES[0].event];
    const bottomCount = stageCounts[FUNNEL_STAGES[FUNNEL_STAGES.length - 1].event];
    const overallRate = topCount > 0 ? (bottomCount / topCount) * 100 : 0;

    // Identify biggest drop-off point
    let worstDropOffIdx = -1;
    let worstDropOff = -1;
    for (let i = 0; i < conversions.length; i++) {
      if (conversions[i].dropOff > worstDropOff) {
        worstDropOff = conversions[i].dropOff;
        worstDropOffIdx = i;
      }
    }

    return { stageCounts, secondaryCounts, conversions, overallRate, totalSessions: topCount, worstDropOffIdx };
  }, [records]);

  // Max count for bar scaling
  const maxCount = useMemo(() => {
    return Math.max(1, ...FUNNEL_STAGES.map((s) => funnelData.stageCounts[s.event] || 0));
  }, [funnelData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#E11D48]" />
        <span className="ml-3 text-[#52525B] text-[14px]">Loading funnel data...</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2
            className="text-[#18181B] text-xl mb-1"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Conversion Funnel
          </h2>
          <p className="text-[14px] text-[#52525B]">
            {records.length} events{' '}
            {funnelData.totalSessions > 0 && (
              <span>from {formatNumber(funnelData.totalSessions)} sessions</span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Time period selector */}
          {(['today', '7d', '30d', 'all'] as TimePeriod[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-[4px] text-[13px] font-medium transition-colors ${
                period === p
                  ? 'bg-[#E11D48] text-white'
                  : 'bg-white border border-[#E4E4E7] text-[#52525B] hover:border-[#E11D48]/40 hover:text-[#E11D48]'
              }`}
            >
              {p === 'today' ? 'Today' : p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : 'All Time'}
            </button>
          ))}

          {/* Refresh */}
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            className="p-1.5 rounded-[4px] border border-[#E4E4E7] text-[#A1A1AA] hover:text-[#E11D48] hover:border-[#E11D48]/40 transition-colors"
            title="Refresh data"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-[#FEF2F2] border border-[#FEE2E2] rounded-[4px] text-[#DC2626] text-[13px]">
          {error}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard
          label="Total Visitors"
          value={formatNumber(funnelData.stageCounts['PAGE_VIEW'] || 0)}
          icon={<BarChart3 size={16} />}
        />
        <SummaryCard
          label="Uploads"
          value={formatNumber(funnelData.stageCounts['LAB_UPLOAD'] || 0)}
          icon={<TrendingUp size={16} className="text-[#8B5CF6]" />}
        />
        <SummaryCard
          label="Purchases"
          value={formatNumber(funnelData.stageCounts['PURCHASE_COMPLETE'] || 0)}
          icon={<TrendingUp size={16} className="text-[#16A34A]" />}
        />
        <SummaryCard
          label="Overall Rate"
          value={formatPercent(funnelData.overallRate)}
          icon={
            funnelData.overallRate >= 5
              ? <TrendingUp size={16} className="text-[#16A34A]" />
              : <TrendingDown size={16} className="text-[#DC2626]" />
          }
        />
      </div>

      {/* Funnel visualization */}
      <div className="bg-white border border-[#E4E4E7] rounded-[4px] p-5 mb-6">
        <h3
          className="text-[#18181B] text-base mb-4"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          Funnel Stages
        </h3>

        <div className="space-y-3">
          {FUNNEL_STAGES.map((stage, idx) => {
            const count = funnelData.stageCounts[stage.event] || 0;
            const widthPct = maxCount > 0 ? Math.max((count / maxCount) * 100, 2) : 2;
            const conversion = idx > 0 ? funnelData.conversions[idx - 1] : null;

            return (
              <div key={stage.event}>
                {/* Conversion arrow between stages */}
                {conversion && (() => {
                  const isWorst = idx - 1 === funnelData.worstDropOffIdx && conversion.dropOff > 0;
                  return (
                    <div className={`flex items-center gap-2 pl-2 mb-1 rounded-[4px] py-1 ${isWorst ? 'bg-[#FEF2F2] border border-[#FEE2E2] px-2 -mx-2' : ''}`}>
                      <svg width="12" height="12" viewBox="0 0 12 12" className={`flex-shrink-0 ${isWorst ? 'text-[#DC2626]' : 'text-[#A1A1AA]'}`}>
                        <path d="M6 2 L6 8 M3 6 L6 9 L9 6" stroke="currentColor" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className={`text-[12px] ${isWorst ? 'text-[#DC2626] font-semibold' : 'text-[#A1A1AA]'}`}>
                        {formatPercent(conversion.rate)} conversion
                        <span className={`ml-2 ${isWorst ? 'text-[#DC2626]' : 'text-[#DC2626]/70'}`}>
                          {formatPercent(conversion.dropOff)} drop-off
                        </span>
                        {isWorst && (
                          <span className="ml-2 text-[11px] bg-[#DC2626] text-white px-1.5 py-0.5 rounded-[4px] font-medium">
                            Biggest drop-off
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })()}

                {/* Stage bar */}
                <div className="flex items-center gap-3">
                  <div className="w-32 flex-shrink-0 text-right">
                    <span className="text-[13px] text-[#52525B]">{stage.label}</span>
                  </div>
                  <div className="flex-1 relative h-8 bg-[#F4F4F5] rounded-[4px] overflow-hidden">
                    <div
                      className="h-full rounded-[4px] transition-all duration-500 ease-out"
                      style={{
                        width: `${widthPct}%`,
                        backgroundColor: stage.color,
                        opacity: 0.85,
                      }}
                    />
                    <div className="absolute inset-0 flex items-center px-3">
                      <span
                        className="text-[13px] font-semibold"
                        style={{
                          color: widthPct > 40 ? '#FFFFFF' : '#18181B',
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      >
                        {formatNumber(count)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Conversion table */}
      <div className="bg-white border border-[#E4E4E7] rounded-[4px] overflow-hidden mb-6">
        <h3
          className="text-[#18181B] text-base px-5 pt-4 pb-3"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          Step-by-Step Conversion
        </h3>

        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[#18181B] text-white">
              <th className="text-left px-5 py-2.5 font-medium">From</th>
              <th className="text-left px-5 py-2.5 font-medium">To</th>
              <th className="text-right px-5 py-2.5 font-medium">Conversion</th>
              <th className="text-right px-5 py-2.5 font-medium">Drop-off</th>
            </tr>
          </thead>
          <tbody>
            {funnelData.conversions.map((c, idx) => {
              const isWorst = idx === funnelData.worstDropOffIdx && c.dropOff > 0;
              return (
              <tr
                key={idx}
                className={`${isWorst ? 'bg-[#FEF2F2]' : idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'}`}
              >
                <td className="px-5 py-2.5 text-[#18181B]">
                  {c.from}
                  {isWorst && <span className="ml-2 text-[11px] bg-[#DC2626] text-white px-1.5 py-0.5 rounded-[4px] font-medium">Biggest drop-off</span>}
                </td>
                <td className="px-5 py-2.5 text-[#18181B]">{c.to}</td>
                <td className="px-5 py-2.5 text-right">
                  <span
                    className={`font-medium ${
                      c.rate >= 50 ? 'text-[#16A34A]' : c.rate >= 20 ? 'text-[#D97706]' : 'text-[#DC2626]'
                    }`}
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {formatPercent(c.rate)}
                  </span>
                </td>
                <td className="px-5 py-2.5 text-right">
                  <span
                    className="text-[#DC2626]/80"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {formatPercent(c.dropOff)}
                  </span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Secondary events */}
      <div className="bg-white border border-[#E4E4E7] rounded-[4px] p-5">
        <h3
          className="text-[#18181B] text-base mb-3"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          Other Events
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {SECONDARY_EVENTS.map((sec) => (
            <div
              key={sec.event}
              className="flex items-center justify-between px-4 py-3 bg-[#FAFAFA] rounded-[4px] border border-[#F4F4F5]"
            >
              <span className="text-[13px] text-[#52525B]">{sec.label}</span>
              <span
                className="text-[14px] font-semibold text-[#18181B]"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {formatNumber(funnelData.secondaryCounts[sec.event] || 0)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Summary card ──

function SummaryCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-[4px] p-4">
      <div className="flex items-center gap-2 mb-2 text-[#A1A1AA]">
        {icon}
        <span className="text-[12px] font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p
        className="text-[20px] font-bold text-[#18181B]"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        {value}
      </p>
    </div>
  );
}
